<template>
  <div>
    <!-- Affiche le Datepicker seulement si le chargement est terminé -->
    <VueDatePicker
      v-if="!loading"
      v-model="selected"
      :highlight="highlightedDates"
      :disabled-dates="disabledDates"
      :locale="frLocale"
      :calendar-class="'w-100'"
      :monday-first="true"
      :clearable="false"
      :format="'dd/MM/yyyy'"
      :placeholder="'Choisissez une date'"
      :enable-time-picker="false"
      auto-apply
      @update:model-value="onDateSelect"
    />
    <!-- Affiche un placeholder pendant le chargement -->
    <div v-else>
        <input type="text" class="form-control" placeholder="Chargement des dates..." disabled>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import fr from 'date-fns/locale/fr'
import axios from 'axios'

const props = defineProps({
  circuitId: { type: [String, Number], required: false },
  voitureId: { type: [String, Number], required: false }
})

// Définir les émissions pour communiquer avec le composant parent
const emit = defineEmits(['dateSelected'])

const loading = ref(true)
const selected = ref(null)
const availableDates = ref([])
const highlightedDates = ref([])
const dateMapping = ref(new Map()) // Map pour stocker la correspondance date -> circuit_date_id et stage info
const frLocale = fr

const disabledDates = (date) => {
  // Formatte la date en YYYY-MM-DD sans conversion de fuseau horaire
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const localDateString = `${year}-${month}-${day}`;
  
  // La date est désactivée si elle N'EST PAS dans la liste des dates disponibles.
  const isDisabled = !availableDates.value.includes(localDateString);
  
  // Pour le débogage, vous pouvez décommenter la ligne suivante :
  // console.log(`Vérification de la date ${localDateString}, désactivée: ${isDisabled}`);
  
  return isDisabled;
}


// Fonction appelée quand une date est sélectionnée
const onDateSelect = (selectedDate) => {
    console.log("Date sélectionnée :", selectedDate);
  if (selectedDate) {
    // Formatte la date sélectionnée en YYYY-MM-DD
    const year = selectedDate.getFullYear();
    const month = (selectedDate.getMonth() + 1).toString().padStart(2, '0');
    const day = selectedDate.getDate().toString().padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    // Récupère les informations de la date depuis le mapping
    const dateInfo = dateMapping.value.get(formattedDate);
    
    if (dateInfo) {
      console.log("Date sélectionnée :", formattedDate);
      console.log("Informations de la date :", dateInfo);
      
      // Émet l'événement vers le composant parent avec toutes les informations
      emit('dateSelected', {
        date: formattedDate,
        dateObject: selectedDate,
        circuitDateId: dateInfo.circuit_date_id,
        stageInfo: dateInfo.stage,
        stageType: dateInfo.stageType, // 'stages' ou 'autreStages'
        allDateInfo: dateInfo
      });
    }
  }
}

onMounted(async () => {
  try {
    let res
    console.log("Chargement des dates pour le circuitId:", props.circuitId, "et voitureId:", props.voitureId);
    if (props.circuitId) {
      res = await axios.get(`/api/configurator/global?circuit=${props.circuitId}`);
    } else if (props.voitureId) {
      res = await axios.get(`/api/configurator/global?voiture=${props.voitureId}`);
    } else {
      return
    }

    const stages = res.data?.datas?.stages || []
    const autreStages = res.data?.datas?.autreStages || []
    let dates = []
    const mapping = new Map()
    
    const extractDates = (stageList, stageType) => {
        stageList.forEach(stage => {
            if (stage.dates) {
                Object.values(stage.dates).forEach(dateObj => {
                    const dateStr = dateObj.date
                    dates.push(dateStr)
                    
                    // Stocke la correspondance avec toutes les informations utiles
                    mapping.set(dateStr, {
                        circuit_date_id: dateObj.circuit_date_id || dateObj.id,
                        stage: stage,
                        stageType: stageType,
                        dateObject: dateObj,
                        stageName: stage.nom || stage.name,
                        stageId: stage.id
                    })
                })
            }
        })
    }

    extractDates(stages, 'stages')
    extractDates(autreStages, 'autreStages')

    availableDates.value = [...new Set(dates)]
    dateMapping.value = mapping

    // --- CORRECTION 2 : Créer les objets Date de manière fiable ---
    highlightedDates.value = availableDates.value.map(d => {
        const [year, month, day] = d.split('-').map(Number);
        // Crée une date à minuit dans le fuseau horaire local (mois est 0-indexé)
        return new Date(year, month - 1, day);
    })

    console.log("Dates disponibles :", availableDates.value);
    console.log("Mapping des dates :", mapping);
    console.log("Dates mises en surbrillance :", highlightedDates.value);

  } catch (error) {
    console.error("Erreur lors du chargement des dates:", error);
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
/* Met en bleu les jours disponibles */
:deep(.dp__cell_highlight) {
  background: #0d6efd !important;
  color: #fff !important;
  border-radius: 50% !important;
}
</style>